// Admin Home Banners — manage the promotional banner carousel on the user home page.
// Banners are stored as {banner_id, image_url, image_path, alt, sort_order, is_active}
// in the home_banners collection. Images upload via /api/upload/image (cloud-backed,
// same pattern as menu items / blog / kitchen images).
import { useEffect, useRef, useState } from "react";
import { API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { ImagePlus, Plus, Pencil, Trash2, Loader2, Upload, ArrowUp, ArrowDown, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";
import { xhrJson } from "../../utils/xhrJson";

const emptyBanner = { image_url: "", image_path: "", alt: "", sort_order: 0, is_active: true };

export default function AdminHomeBanners() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/home-banners`, { credentials: "include" });
      setRows(r.ok ? await r.json() : []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      let r;
      try {
        r = await fetch(`${API}/upload/image`, { method: "POST", credentials: "include", body: fd });
      } catch (netErr) {
        throw new Error(`Network error: ${netErr.message || "could not reach server"}`);
      }
      if (!r.ok) {
        // Try JSON first, fall back to plain text — some upstream errors
        // (proxy/gateway timeouts, HTML error pages) won't parse as JSON.
        let detail = "";
        try {
          const e = await r.clone().json();
          detail = e.detail || JSON.stringify(e);
        } catch {
          try { detail = (await r.text()).slice(0, 200); } catch { /* ignore */ }
        }
        throw new Error(`${r.status} ${r.statusText}${detail ? ` — ${detail}` : ""}`);
      }
      const data = await r.json();
      setEditing((p) => ({ ...p, image_url: data.url, image_path: data.path }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.message || "Upload failed");
    }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!editing.image_url) { toast.error("Upload an image first"); return; }
    if (!editing.alt?.trim()) { toast.error("Alt text is required"); return; }
    setSaving(true);
    try {
      const isNew = !editing.banner_id;
      const url = isNew ? `${API}/admin/home-banners` : `${API}/admin/home-banners/${editing.banner_id}`;
      const payload = {
        image_url: editing.image_url,
        image_path: editing.image_path,
        alt: editing.alt.trim(),
        sort_order: Number(editing.sort_order) || 0,
        is_active: !!editing.is_active,
      };
      const { ok, data } = await xhrJson(isNew ? "POST" : "PUT", url, payload);
      if (!ok) throw new Error(data.detail || "Save failed");
      toast.success(isNew ? "Banner added" : "Banner updated");
      setEditing(null); load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (banner_id) => {
    if (!window.confirm("Delete this banner? This cannot be undone.")) return;
    try {
      const { ok, data } = await xhrJson("DELETE", `${API}/admin/home-banners/${banner_id}`);
      if (!ok) throw new Error(data.detail || "Delete failed");
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e.message); }
  };

  const handleMove = async (row, delta) => {
    const newSort = (row.sort_order || 0) + delta;
    try {
      const { ok, data } = await xhrJson("PUT", `${API}/admin/home-banners/${row.banner_id}`, { sort_order: newSort });
      if (!ok) throw new Error(data.detail || "Update failed");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const handleToggleActive = async (row) => {
    try {
      const { ok, data } = await xhrJson("PUT", `${API}/admin/home-banners/${row.banner_id}`, { is_active: !row.is_active });
      if (!ok) throw new Error(data.detail || "Update failed");
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <AdminLayout activeNav="home-banners">
      <div className="p-4 sm:p-6 space-y-4" data-testid="admin-home-banners">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-pink-100 text-pink-700 flex items-center justify-center">
              <ImagePlus size={18} />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-extrabold text-gray-900">Home Banners</h1>
              <p className="text-xs text-gray-500">Promotional images shown on the user home carousel (3:4 aspect, rendered in a 2-column grid).</p>
            </div>
          </div>
          <Button
            onClick={() => setEditing({ ...emptyBanner, sort_order: rows.length })}
            className="bg-pink-600 hover:bg-pink-700 text-white"
            data-testid="home-banner-new-btn"
          >
            <Plus size={14} className="mr-1" /> New banner
          </Button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-400 bg-white rounded-2xl border border-gray-100">
            <Loader2 className="animate-spin inline" size={16} />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm bg-white rounded-2xl border border-gray-100">
            No banners yet. Add the first one — it&apos;ll show up on the user home page right away.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {rows.map((b) => (
              <div
                key={b.banner_id}
                className={`bg-white rounded-2xl border overflow-hidden ${b.is_active ? "border-gray-100" : "border-amber-200 opacity-70"}`}
                data-testid={`home-banner-row-${b.banner_id}`}
              >
                <div className="relative aspect-[3/4] bg-gray-50">
                  <img src={b.image_url} alt={b.alt} className="absolute inset-0 w-full h-full object-cover" />
                  {!b.is_active && (
                    <div className="absolute top-2 left-2 bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">Hidden</div>
                  )}
                  <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">#{b.sort_order ?? 0}</div>
                </div>
                <div className="p-3 space-y-2">
                  <p className="text-sm font-bold text-gray-900 truncate" title={b.alt}>{b.alt}</p>
                  <div className="flex items-center gap-1 justify-between">
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => handleMove(b, -1)} title="Move up" data-testid={`home-banner-up-${b.banner_id}`}><ArrowUp size={11} /></Button>
                      <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => handleMove(b, 1)} title="Move down" data-testid={`home-banner-down-${b.banner_id}`}><ArrowDown size={11} /></Button>
                      <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => handleToggleActive(b)} title={b.is_active ? "Hide" : "Show"} data-testid={`home-banner-toggle-${b.banner_id}`}>
                        {b.is_active ? <EyeOff size={11} /> : <Eye size={11} />}
                      </Button>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => setEditing({ ...b })} data-testid={`home-banner-edit-${b.banner_id}`}><Pencil size={11} /></Button>
                      <Button size="sm" variant="outline" className="h-7 text-[10px] text-red-600 border-red-200" onClick={() => handleDelete(b.banner_id)} data-testid={`home-banner-delete-${b.banner_id}`}><Trash2 size={11} /></Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing?.banner_id ? "Edit banner" : "New banner"}</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">Upload a 3:4 image (portrait). It will appear in the home carousel.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Image *</Label>
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-3 bg-gray-50 space-y-2">
                  {editing.image_url ? (
                    <div className="relative aspect-[3/4] max-h-60 mx-auto bg-white rounded-lg overflow-hidden">
                      <img src={editing.image_url} alt="preview" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="aspect-[3/4] max-h-60 mx-auto bg-white rounded-lg flex items-center justify-center text-xs text-gray-400">
                      No image yet
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleUpload(e.target.files?.[0])}
                    data-testid="home-banner-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full text-xs"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="home-banner-upload-btn"
                  >
                    {uploading ? <><Loader2 size={12} className="mr-1 animate-spin" /> Uploading…</> : <><Upload size={12} className="mr-1" /> {editing.image_url ? "Replace image" : "Upload image"}</>}
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Alt text *</Label>
                <Input
                  value={editing.alt || ""}
                  onChange={(e) => setEditing((p) => ({ ...p, alt: e.target.value }))}
                  placeholder="e.g. Meal Bowls"
                  data-testid="home-banner-form-alt"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Sort order</Label>
                  <Input
                    type="number"
                    value={editing.sort_order ?? 0}
                    onChange={(e) => setEditing((p) => ({ ...p, sort_order: e.target.value }))}
                    data-testid="home-banner-form-sort"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Active</Label>
                  <div className="h-9 flex items-center">
                    <Switch
                      checked={!!editing.is_active}
                      onCheckedChange={(v) => setEditing((p) => ({ ...p, is_active: v }))}
                      data-testid="home-banner-form-active"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="home-banner-form-save">
              {saving ? <Loader2 size={13} className="animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
